import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import clsx from 'clsx';
import { useApolloClient, useMutation, useQuery } from 'react-apollo-hooks';
import {
  confirmInstallationPhotoUploadMutation,
  installationPhotoUploadRequestQuery,
  installationPhotosQuery,
  installationQuery,
  removeInstallationPhotoMutation,
  updateInstallationPhotoLabelMutation,
  updateInstallationSectionMutation,
} from '../../graphql/schema/installation';
import {
  INSTALLATION_SECTIONS,
  isSectionComplete,
  parseInstallationAnswers,
  sectionFieldsWithoutPhotos,
} from '../../util/installationSections';

const IDLE_MS = 15 * 60 * 1000;

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.background.default,
  },
  nav: {
    [theme.breakpoints.up('md')]: {
      width: '16rem',
      flexShrink: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  navItemSelected: {
    backgroundColor: theme.palette.action.selected,
  },
  navCheck: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
  },
  navIdle: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.disabled,
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 3),
  },
  dropzone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
  },
  thumbnail: {
    position: 'relative',
    width: theme.spacing(14),
    height: theme.spacing(14),
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbnailDelete: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    padding: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
  },
  thumbnailLabel: {
    width: theme.spacing(14),
  },
}));

const InstallationApp = ({ organizationId, onIdle, onAccessError }) => {
  const classes = useStyles();
  const client = useApolloClient();
  const [selectedId, setSelectedId] = useState(INSTALLATION_SECTIONS[0].id);
  const [values, setValues] = useState({});
  const [uploadErrors, setUploadErrors] = useState([]);
  const fileInput = useRef(null);
  const idleTimer = useRef(null);

  const bumpIdle = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => onIdle && onIdle(), IDLE_MS);
  }, [onIdle]);

  useEffect(() => {
    bumpIdle();
    const onActivity = () => bumpIdle();
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('touchstart', onActivity);
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('touchstart', onActivity);
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
    };
  }, [bumpIdle]);

  const { data, loading, error, refetch } = useQuery(installationQuery, {
    variables: { organizationId },
    fetchPolicy: 'network-only',
  });

  // The pin check at login is global, so a code issued for another hospital still "logs in"
  // here — but every operation is then rejected as Unauthorized. Kick back to the code
  // screen instead of showing an editable form whose writes silently fail.
  const unauthorized = !!error && /unauthorized/i.test(error.message || '');
  useEffect(() => {
    if (unauthorized && onAccessError) onAccessError();
  }, [unauthorized, onAccessError]);
  const { data: photosData, refetch: refetchPhotos } = useQuery(installationPhotosQuery, {
    variables: { organizationId },
    fetchPolicy: 'network-only',
  });

  const updateSection = useMutation(updateInstallationSectionMutation);
  const confirmPhoto = useMutation(confirmInstallationPhotoUploadMutation);
  const updatePhotoLabel = useMutation(updateInstallationPhotoLabelMutation);
  const removePhoto = useMutation(removeInstallationPhotoMutation);

  useEffect(() => {
    const answers = parseInstallationAnswers(data && data.installation && data.installation.answers);
    const next = {};
    INSTALLATION_SECTIONS.forEach(section => {
      next[section.id] = sectionFieldsWithoutPhotos(answers[section.id]);
    });
    setValues(next);
  }, [data]);

  const photosBySection = useMemo(() => {
    const grouped = {};
    ((photosData && photosData.installationPhotos) || []).forEach(photo => {
      grouped[photo.section] = grouped[photo.section] || [];
      grouped[photo.section].push({
        fileName: photo.fileName,
        url: photo.url,
        label: photo.label || '',
        isPdf: String(photo.fileName).toLowerCase().endsWith('.pdf'),
      });
    });
    return grouped;
  }, [photosData]);

  const selectedIndex = INSTALLATION_SECTIONS.findIndex(section => section.id === selectedId);
  const selected = INSTALLATION_SECTIONS[selectedIndex];
  const selectedPhotos = photosBySection[selected.id] || [];

  const persistSection = sectionId => {
    const fields = values[sectionId] || {};
    return updateSection({
      variables: { organizationId, section: sectionId, answers: JSON.stringify(fields) },
    });
  };

  const handleFieldChange = (sectionId, fieldKey) => e => {
    const value = e.target.value;
    setValues(prev => ({ ...prev, [sectionId]: { ...prev[sectionId], [fieldKey]: value } }));
  };

  const handleAddPhotos = async e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const failed = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      try {
        const result = await client.query({
          query: installationPhotoUploadRequestQuery,
          variables: {
            organizationId,
            section: selected.id,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
          },
          fetchPolicy: 'network-only',
        });
        const upload = result.data && result.data.installationPhotoUploadRequest;
        if (!upload || !upload.url) throw new Error('No upload URL');
        const response = await fetch(upload.url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!response.ok) throw new Error(`Upload failed (${response.status})`);
        await confirmPhoto({
          variables: { organizationId, section: selected.id, fileName: upload.fileName, label: '' },
        });
      } catch (uploadError) {
        failed.push(file.name);
      }
    }
    setUploadErrors(failed);
    refetchPhotos();
  };

  const sectionComplete = section =>
    isSectionComplete(section, values[section.id], (photosBySection[section.id] || []).length);

  const completedCount = INSTALLATION_SECTIONS.filter(sectionComplete).length;

  if (loading && !data) {
    return (
      <Box py={8} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box py={8} display="flex" flexDirection="column" alignItems="center">
        <Typography color="error" gutterBottom>
          {unauthorized
            ? 'This code is not valid for this hospital.'
            : 'Could not load the installation. Check your connection.'}
        </Typography>
        {!unauthorized && (
          <Button color="primary" variant="outlined" onClick={() => refetch()}>
            Retry
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Installation</Typography>
        <Chip size="small" label={`${completedCount} / ${INSTALLATION_SECTIONS.length}`} />
      </Box>
      <Box display="flex" flexGrow={1} style={{ flexDirection: window.innerWidth < 960 ? 'column' : 'row' }}>
        <Box className={classes.nav}>
          {INSTALLATION_SECTIONS.map(section => (
            <Box
              key={section.id}
              className={clsx(classes.navItem, { [classes.navItemSelected]: section.id === selectedId })}
              onClick={() => {
                persistSection(selected.id);
                setSelectedId(section.id);
              }}
            >
              <Icon className={sectionComplete(section) ? classes.navCheck : classes.navIdle}>
                {sectionComplete(section) ? 'check_circle' : 'radio_button_unchecked'}
              </Icon>
              <Typography variant="body2">{section.name}</Typography>
            </Box>
          ))}
        </Box>
        <Box className={classes.content}>
          <Typography variant="h4" gutterBottom>
            {selected.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {selected.description}
          </Typography>
          <Box mt={2}>
            <Grid container spacing={2}>
              {selected.fields.map(field => (
                <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.key}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={field.label}
                    type={field.type || 'text'}
                    multiline={field.multiline}
                    rows={field.multiline ? 3 : undefined}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    value={(values[selected.id] && values[selected.id][field.key]) || ''}
                    onChange={handleFieldChange(selected.id, field.key)}
                    onBlur={() => persistSection(selected.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          {selected.hasPhotos && (
            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                Photos
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Labels appear on the photos in the generated document.
              </Typography>
              <Box display="flex" flexWrap="wrap">
                {selectedPhotos.map(photo => (
                  <Box key={photo.fileName} m={0.5}>
                    <Box className={classes.thumbnail}>
                      {photo.isPdf ? (
                        <Tooltip title={photo.fileName}>
                          <Icon color="primary">picture_as_pdf</Icon>
                        </Tooltip>
                      ) : (
                        <img src={photo.url} alt={photo.label || photo.fileName} className={classes.thumbnailImage} />
                      )}
                      <IconButton
                        size="small"
                        className={classes.thumbnailDelete}
                        onClick={() =>
                          removePhoto({
                            variables: { organizationId, section: selected.id, fileName: photo.fileName },
                          }).then(() => refetchPhotos())
                        }
                      >
                        <Icon fontSize="small">close</Icon>
                      </IconButton>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Label"
                      defaultValue={photo.label}
                      className={classes.thumbnailLabel}
                      onBlur={e =>
                        updatePhotoLabel({
                          variables: {
                            organizationId,
                            section: selected.id,
                            fileName: photo.fileName,
                            label: e.target.value,
                          },
                        }).then(() => refetchPhotos())
                      }
                    />
                  </Box>
                ))}
                <Box
                  className={classes.dropzone}
                  minWidth={160}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => fileInput.current && fileInput.current.click()}
                >
                  <Icon color="primary">add_a_photo</Icon>
                  <Typography variant="body2" color="textSecondary">
                    Tap to add photos
                  </Typography>
                </Box>
              </Box>
              {uploadErrors.length > 0 && (
                <Typography variant="body2" color="error">
                  Failed to upload: {uploadErrors.join(', ')}. Please try again.
                </Typography>
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*,application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleAddPhotos}
              />
            </Box>
          )}
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              disabled={selectedIndex === 0}
              onClick={() => {
                persistSection(selected.id);
                setSelectedId(INSTALLATION_SECTIONS[selectedIndex - 1].id);
              }}
            >
              Back
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                persistSection(selected.id).then(() => refetch());
                if (selectedIndex < INSTALLATION_SECTIONS.length - 1) {
                  setSelectedId(INSTALLATION_SECTIONS[selectedIndex + 1].id);
                }
              }}
            >
              {selectedIndex === INSTALLATION_SECTIONS.length - 1 ? 'Save' : 'Save & Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InstallationApp;
